import { Inject, Injectable, Optional } from '@angular/core';
import { MessageResourceManager } from '../../resources/message-resource-manager';
import { MessageCodes } from '../../resources/message-codes';
import { ModalService, ShowCustomModalWithResulInterface, ShowExceptionPopUpInterface, ShowExternalModalInterface, ShowMessageInterface, ShowPopUpModalInterface, ShowStandardModalInterface } from '../../view-models/modal/modal.service';
import { MessageButton } from '../../view-models/modal/message-button';
import { MessageResult } from '../../view-models/modal/message-result';
import { ModalViewModelInterface } from '../../view-models/modal/modal-view-model.interface';
import { ModalResult } from '../../view-models/modal/modal-result';
import { Information } from '../../messages/information';
import { BaseError } from '../../messages/base-error';
import { PopupViewModel } from '../../view-models/modal/popup-view-model';
import { ModalContainerComponent } from './modal-container.component';
import { ExternalModalViewModel } from '../../view-models/modal/external-modal-view-model';
import { ZoomResult } from '../../domain-models/zoom/zoom-result';
import { ZoomOrchestratorViewModel } from '../../view-models/zoom/zoom-orchestrator-view-model';
import { ZoomUIStarterArgs } from '../../view-models/zoom/zoom-ui-starter-args';
import { IFrameGetter } from '../../starter/ui-starter';
import { EnvironmentConfiguration, UNPROXIED_ENVIRONMENT } from '@nts/std/environments';
import { EnvironmentHelper } from '@nts/std/environments';
import { MetaDataDescriptions } from '../../meta-data/meta-data-descriptions';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RoutingService } from '../../routing/routing.service';
import { AuthService } from '../../auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ExternalModalViewModelInterface } from '../../view-models/modal/external-modal-view-model.interface';
import { AuthQueryParams } from '../../auth/auth.const';

@Injectable()
export class ModalComponentService extends ModalService {

  constructor(
    public dialog: MatDialog,
    // private readonly bsModalService: BsModalService,
    private routingService: RoutingService,
    public override route: ActivatedRoute,
    private readonly environment: EnvironmentConfiguration,
    private readonly authService: AuthService,
    @Optional()
    @Inject(UNPROXIED_ENVIRONMENT) private readonly unproxiedEnvironment: EnvironmentConfiguration
  ) {
    super();
  }

  async showStandardModalAsync(
  buttons: MessageButton | ShowStandardModalInterface,
  title: string = '',
  viewModel: ModalViewModelInterface<any, void> = undefined,
  config: MatDialogConfig<any> = { disableClose: true },
  metaDataDescriptionsButtons: MetaDataDescriptions[] = [],
  isCentered = false
): Promise<MessageResult> {
  // Destruttura solo se oggetto (non MessageButton/number)
  let currentButtons = buttons as MessageButton;
  let currentTitle = title;
  let currentViewModel = viewModel;
  let currentConfig = config;
  let currentMetaDataDescriptionsButtons = metaDataDescriptionsButtons;
  let currentIsCentered = isCentered;

  if (typeof buttons !== 'number') {  // Strict !==, assumendo MessageButton è enum number
    const obj = buttons as ShowStandardModalInterface;
    currentButtons = obj.buttons ?? MessageButton.Ok;  // Fallback default
    currentTitle = obj.title ?? currentTitle;
    currentViewModel = obj.viewModel ?? currentViewModel;
    currentConfig = obj.config ?? currentConfig;
    currentMetaDataDescriptionsButtons = obj.metaDataDescriptionsButtons ?? currentMetaDataDescriptionsButtons;
    currentIsCentered = obj.isCentered ?? currentIsCentered;
  }

  if (this.ovm) {
    this.ovm.isModalOpen = true;
  }

  // Config updates (unchanged, ma usa currentConfig)
  if (this.routingService.inIframe) {
    currentConfig.backdropClass = 'in-iframe';
  }

  if (this.routingService.inIframe) {
    const inIframeDialogClass = 'in-iframe';
    const panelClass = currentConfig.panelClass?.length > 0 
      ? [inIframeDialogClass, ...currentConfig.panelClass] 
      : [inIframeDialogClass];
    currentConfig = { ...currentConfig, panelClass };
  }

  if (currentIsCentered === false) {
    const top = `${this.routingService.inIframe ? 50 : 100}px`;
    if (currentConfig.position?.top == null) {
      const position = { ... (currentConfig.position ?? { top: null }), top };
      currentConfig = { ...currentConfig, position };
    }
  }

  const dialogRef = this.dialog.open(ModalContainerComponent, currentConfig);

  const promise = dialogRef.componentInstance.showStandardModalAsync(
    currentButtons,
    currentTitle,
    currentViewModel,
    currentMetaDataDescriptionsButtons
  );

  promise.then(async () => {
    if (this.ovm) {
      this.ovm.isModalOpen = false;
    }
  });

  return promise;
}

  async showZoomAsync(
    viewModel: ZoomOrchestratorViewModel,
    maximizable = true,
    panelClass: string = 'zoom-container',
    width: string|undefined = undefined
  ): Promise<ModalResult<ZoomResult>> {
    viewModel.modalTitle = MessageResourceManager.Current.getMessageWithArgs(MessageCodes.Zoom) + ' -> ' + viewModel.rootDomainModelDisplayName;

    return await this.showCustomModalWithResultAsync<ZoomUIStarterArgs, ZoomResult>({
      viewModel,
      showCancel: false,
      maximizable,
      maximized: true,
      config: {
        disableClose: true,
        panelClass,
        width
      },
      isCentered: true
    } as ShowCustomModalWithResulInterface<ZoomResult>);
  }

  async showMessageAsync(
  title: string | ShowMessageInterface,
  message: string = '',
  buttons: MessageButton = MessageButton.Ok,
  metaDataDescriptionsButtons: MetaDataDescriptions[] = [],
  panelClass = 'message-modal',
  width: string | undefined = undefined
): Promise<MessageResult> {
  // Destruttura solo se oggetto, con fallback safe
  let currentTitle = title as string;  // Default se è già una stringa
  let currentMessage = message;
  let currentButtons = buttons;
  let currentMetaDataDescriptionsButtons = metaDataDescriptionsButtons;
  let currentPanelClass = panelClass;
  let currentWidth = width;

  if (typeof title !== 'string') {  // Strict !==
    const obj = title as ShowMessageInterface;
    currentTitle = obj.title ?? '';  // Nullish safe
    currentMessage = obj.message ?? currentMessage;
    currentButtons = obj.buttons ?? currentButtons;
    currentMetaDataDescriptionsButtons = obj.metaDataDescriptionsButtons ?? currentMetaDataDescriptionsButtons;
    currentPanelClass = obj.panelClass ?? currentPanelClass;
    currentWidth = obj.width;
  }

  const top = `${this.routingService.inIframe ? 50 : 100}px`;

  const viewModel = new PopupViewModel();
  viewModel.list = [{
    message: currentMessage,
    detail: null
  }];

  return await this.showStandardModalAsync({
    buttons: currentButtons,
    title: currentTitle,
    viewModel,
    config: {
      position: { top },
      width: currentWidth,
      panelClass: currentPanelClass
    },
    metaDataDescriptionsButtons: currentMetaDataDescriptionsButtons
  });
}

  /**
   * Utilizzare per visualizzare popup partendo da un errore
   */
  async showExceptionPopUp(
    error: Error | ShowExceptionPopUpInterface,
    caption = '',
    message = '',
    metaDataDescriptionsButton: MetaDataDescriptions = undefined,
    panelClass = 'exception-modal',
    width: string | undefined = undefined
  ): Promise<MessageResult> {  // Assumi tipo da showStandardModalAsync

    // Destruttura solo se interfaccia (non Error)
    let currentError = error as Error;
    let currentCaption = caption;
    let currentMessage = message;
    let currentMetaDataDescriptionsButton = metaDataDescriptionsButton;
    let currentPanelClass = panelClass;
    let currentWidth = width;

    if (!(error instanceof Error)) {
      const obj = error as ShowExceptionPopUpInterface;
      currentError = obj.error ?? new Error('Unknown error');  // Fallback safe
      currentCaption = obj.caption ?? currentCaption;
      currentMessage = obj.message ?? currentMessage;
      currentMetaDataDescriptionsButton = obj.metaDataDescriptionsButton ?? currentMetaDataDescriptionsButton;
      currentPanelClass = obj.panelClass ?? currentPanelClass;
      currentWidth = obj.width;
    }

    const viewModel = new PopupViewModel();
    viewModel.list = [{
      message: currentMessage || currentError.message,
      detail: this.getExceptionDetail(currentError)
    }];

    viewModel.caption = currentCaption || this.errorCaption;

    return await this.showStandardModalAsync({
      buttons: MessageButton.Ok,
      title: viewModel.caption,
      viewModel,
      config: {
        disableClose: true,
        panelClass: currentPanelClass,
        width: currentWidth
      },
      metaDataDescriptionsButtons: currentMetaDataDescriptionsButton ? [currentMetaDataDescriptionsButton] : []
    });
  }

  async showCustomModalWithResultAsync<TInputArgs, TResult>(
    viewModel: ModalViewModelInterface<TInputArgs, TResult> | ShowCustomModalWithResulInterface<TResult>,
    showCancel = true,
    maximizable = false,
    maximized = false,
    config: MatDialogConfig<any> = {},
    isCentered = false
  ): Promise<ModalResult<TResult>> {

    // Destruttura solo se interfaccia (ha .viewModel)
    let currentViewModel = viewModel;
    let currentShowCancel = showCancel;
    let currentMaximizable = maximizable;
    let currentMaximized = maximized;
    let currentConfig = config;
    let currentIsCentered = isCentered;

    if ('viewModel' in (viewModel as any)) {  // Check safe: ha proprietà .viewModel?
      const obj = viewModel as ShowCustomModalWithResulInterface<TResult>;
      currentViewModel = obj.viewModel ?? currentViewModel;  // Fallback
      currentShowCancel = obj.showCancel ?? currentShowCancel;
      currentMaximizable = obj.maximizable ?? currentMaximizable;
      currentMaximized = obj.maximized ?? currentMaximized;
      currentConfig = obj.config ?? currentConfig;
      currentIsCentered = obj.isCentered ?? currentIsCentered;
    }

    if (this.ovm) {
      this.ovm.isModalOpen = true;
    }

    let finalConfig = { ...currentConfig };

    // Aggiorna backdrop
    if (this.routingService.inIframe) {
      finalConfig.backdropClass = 'in-iframe';
    }

    // Aggiunge classi panel (fullscreen + iframe)
    const panelClasses: string[] = [];
    if (currentMaximized) {
      panelClasses.push('fullscreen-dialog');
    }
    if (this.routingService.inIframe) {
      panelClasses.push('in-iframe');
    }
    if (panelClasses.length > 0) {
      const existingPanelClass = finalConfig.panelClass;
      if (typeof existingPanelClass === 'string' && existingPanelClass) {
        finalConfig.panelClass = [existingPanelClass, ...panelClasses];
      } else {
        finalConfig.panelClass = panelClasses.concat(existingPanelClass as string[]);
      }
    }

    // Position logic
    if (currentIsCentered === false || currentMaximized) {
      const top = currentMaximized 
        ? `${this.routingService.inIframe ? 25 : 55}px`
        : `${this.routingService.inIframe ? 50 : 100}px`;
      if (finalConfig.position?.top == null) {
        finalConfig.position = { ... (finalConfig.position ?? {}), top };
      }
    }

    const dialogRef = this.dialog.open(ModalContainerComponent, finalConfig);

    const promise = dialogRef.componentInstance.showCustomModalWithResultAsync<TResult>(
      currentViewModel as ModalViewModelInterface<any, TResult>,
      currentShowCancel,
      currentMaximizable,
      currentMaximized
    );

    promise.then(async () => {
      if (this.ovm) {
        this.ovm.isModalOpen = false;
      }
    });

    return promise;
  }

  async showExternalModal<TResult>(
  baseUrl: string | ShowExternalModalInterface<TResult>,
  jsonIdentity: string | undefined = undefined,
  additionalQueryParams: URLSearchParams = new URLSearchParams(),
  modalTitle: string = '',
  externalReturn = true,
  supportRemoteClosingCheck = false,
  customExternalModalViewModel: ExternalModalViewModelInterface<any, TResult> | undefined = undefined,
  supportRemoteStatus = false,
  panelClass = 'external-modal-container',
  width: string | undefined = undefined
): Promise<ModalResult<TResult>> {

  // Destruttura solo se interfaccia (non string)
  let currentBaseUrl = baseUrl as string;
  let currentJsonIdentity = jsonIdentity;
  let currentAdditionalQueryParams = additionalQueryParams;
  let currentModalTitle = modalTitle;
  let currentExternalReturn = externalReturn;
  let currentSupportRemoteClosingCheck = supportRemoteClosingCheck;
  let currentCustomExternalModalViewModel = customExternalModalViewModel;
  let currentSupportRemoteStatus = supportRemoteStatus;
  let currentPanelClass = panelClass;
  let currentWidth = width;

  if (typeof baseUrl !== 'string') {
    const obj = baseUrl as ShowExternalModalInterface<TResult>;
    currentBaseUrl = obj.baseUrl ?? '';  // Fallback string vuota
    currentJsonIdentity = obj.jsonIdentity ?? currentJsonIdentity;
    currentAdditionalQueryParams = obj.additionalQueryParams ?? currentAdditionalQueryParams;
    currentModalTitle = obj.modalTitle ?? currentModalTitle;
    currentExternalReturn = obj.externalReturn ?? currentExternalReturn;
    currentSupportRemoteClosingCheck = obj.supportRemoteClosingCheck ?? currentSupportRemoteClosingCheck;
    currentCustomExternalModalViewModel = obj.customExternalModalViewModel ?? currentCustomExternalModalViewModel;
    currentSupportRemoteStatus = obj.supportRemoteStatus ?? currentSupportRemoteStatus;
    currentPanelClass = obj.panelClass ?? currentPanelClass;
    currentWidth = obj.width;
  }

  const tenantId = await this.authService.getTenantId();
  const enterpriseData = await this.authService.getEnterpriseData(tenantId);

  const viewModel = currentCustomExternalModalViewModel ?? new ExternalModalViewModel(this);
  viewModel.baseUrl = EnvironmentHelper.getProxiedUrl(
    currentBaseUrl, this.environment, this.unproxiedEnvironment
  );

  viewModel.jsonIdentity = currentJsonIdentity;
  viewModel.additionalQueryParams = currentAdditionalQueryParams ?? new URLSearchParams();

  if (enterpriseData?.enterpriseId != null && enterpriseData.enterpriseId > -1) {
    viewModel.additionalQueryParams.set(AuthQueryParams.ENTERPRISE_ID_QUERY_KEY, enterpriseData.enterpriseId.toString());
  }

  if (enterpriseData?.companyId != null && enterpriseData.companyId > -1) {
    viewModel.additionalQueryParams.set(AuthQueryParams.COMPANY_ID_QUERY_KEY, enterpriseData.companyId.toString());
  }

  viewModel.modalTitle = currentModalTitle;
  viewModel.externalReturn = currentExternalReturn;
  viewModel.supportRemoteClosingCheck = currentSupportRemoteClosingCheck;
  viewModel.supportRemoteStatus = currentSupportRemoteStatus;

  const res = await this.showCustomModalWithResultAsync<IFrameGetter, TResult>({
    viewModel,
    showCancel: true,
    maximizable: false,
    maximized: false,
    config: {
      panelClass: currentPanelClass,
      width: currentWidth
    },
    isCentered: true
  } as ShowCustomModalWithResulInterface<TResult>);

  return res;
}

  /**
   * Utilizzare per visualizzare popup partendo da una response
   */
  async showPopUp(
    informations: Array<Information> | ShowPopUpModalInterface,
    errors: Array<BaseError> = [],
    caption = '',
    metaDataDescriptionsButton: MetaDataDescriptions | undefined = undefined,
    panelClass = 'popup-modal',
    width: string | undefined = undefined
  ): Promise<MessageResult> {  // Assumi tipo da showStandardModalAsync

    // Destruttura solo se interfaccia (non array)
    let currentInformations = informations as Array<Information>;
    let currentErrors = errors;
    let currentCaption = caption;
    let currentMetaDataDescriptionsButton = metaDataDescriptionsButton;
    let currentPanelClass = panelClass;
    let currentWidth = width;

    if (!Array.isArray(informations)) {
      const obj = informations as ShowPopUpModalInterface;
      currentInformations = obj.informations ?? [];  // Fallback array vuoto
      currentErrors = obj.errors ?? currentErrors;
      currentCaption = obj.caption ?? currentCaption;
      currentMetaDataDescriptionsButton = obj.metaDataDescriptionsButton ?? currentMetaDataDescriptionsButton;
      currentPanelClass = obj.panelClass ?? currentPanelClass;
      currentWidth = obj.width;
    }

    const viewModel = new PopupViewModel();

    if (currentErrors.length > 0) {
      viewModel.caption = currentCaption || this.errorCaption;

      const errorList = currentErrors.map((e) => {
        let detail = '';
        detail = this.visitErrors(detail, e);

        let message = e.description;
        if (e.code?.length > 0) {
          message += `<br>Codice: ${e.code}`;
        }
        if (e.propertyName?.length > 0) {
          message += `<br>PropertyName: ${e.propertyName}`;
        }
        return { message, detail };
      });

      viewModel.list = errorList;

    } else if (currentInformations.length > 0) {
      viewModel.caption = currentCaption || this.informationCaption;

      const infoList = currentInformations.map((i) => {
        let detail = '';  // Fix: usa let e assegna
        this.visitInformations(detail, i);
        return {
          message: `Information: ${i.description}<br>Code: ${i.code}<br>PropertyName: ${i.propertyName}<br>`,
          detail
        };
      });

      viewModel.list = infoList;
    }

    return await this.showStandardModalAsync({
      buttons: MessageButton.Ok,
      title: viewModel.caption,
      viewModel,
      metaDataDescriptionsButtons: currentMetaDataDescriptionsButton ? [currentMetaDataDescriptionsButton] : [],
      config: {
        panelClass: currentPanelClass,
        disableClose: true,
        width: currentWidth
      }
    });
  }

  private get informationCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Information);
  };

  private get warningCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Warning);
  }

  private get errorCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Error);
  }

  private visitErrors(sb: string, error: BaseError): string {
    if (error != null) {
      if (error.objectName && error.objectName?.length > 0) {
        sb += `${error.objectName}<br>`;
      }

      if (error.stackTrace && error.stackTrace?.length > 0) {
        sb += `${error.stackTrace}<br>`;
      }
      return this.visitErrors(sb, error.innerError);
    }
    return sb;
  }

  private visitInformations(sb: string, information: Information) {
    if (information != null) {
      const errorFormat = `DMName: ${information.objectName}<br>`;
      sb += errorFormat;
    }
  }

  private getExceptionDetail(exception: Error) {
    let detail = '';

    if (exception != null) {
      let sb = '';
      sb += `Message: ${exception.message} <br>`;
      sb += `Stack trace: ${exception.stack} <br>`;

      detail = sb;
    }

    return detail;
  }
}

